import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import type { Session } from '@supabase/supabase-js';
import { DeveloperUtilities } from '@/lib/utils/DeveloperUtilities';
import { contentReportManager } from '@/lib/managers/ContentReportManager';
import { reportContextStore } from '@/lib/report/ReportContextStore';
import type { SummaryAction, SummaryPreferences, HomeSummary } from '@/types/home-summary';

type UseHomeScreenHandlersParams = {
  session: Session | null;
  preferences: SummaryPreferences | null;
  setSummary: (summary: HomeSummary | null) => void;
  setIsDeveloperGenerating: (generating: boolean) => void;
};

/**
 * Custom hook to encapsulate event handlers for the home screen
 */
export function useHomeScreenHandlers({
  session,
  preferences,
  setSummary,
  setIsDeveloperGenerating,
}: UseHomeScreenHandlersParams) {
  const router = useRouter();

  /**
   * Handle summary action button presses (CTAs)
   */
  const handleSummaryAction = useCallback(
    (action: SummaryAction) => {
      switch (action.type) {
        case 'open-calendar':
          router.push('/calendar');
          break;
        case 'open-tasks':
          router.push('/todo');
          break;
        case 'navigate': {
          if (action.params) {
            const params = Object.fromEntries(
              Object.entries(action.params).map(([key, value]) => [key, String(value)])
            );
            // action.route comes from server-generated summary data, not statically known
            router.push({ pathname: action.route as never, params });
          } else {
            router.push(action.route as never);
          }
          break;
        }
        default:
          break;
      }
    },
    [router]
  );

  /**
   * Report an AI-generated summary. Stashes the exact content, then routes to
   * the shared report screen (lives under Settings) where the user can add a
   * reason and submit.
   */
  const handleReportSummary = useCallback(
    (summary: HomeSummary) => {
      reportContextStore.set(contentReportManager.buildSummaryContext(summary));
      router.push('/report-ai-content');
    },
    [router],
  );

  /**
   * Developer trigger for generating summaries and notifications
   */
  const handleDeveloperTrigger = useCallback(async () => {
    if (!session?.access_token) {
      console.warn('[useHomeScreenHandlers] No session token available');
      return;
    }

    const devUtils = DeveloperUtilities.getInstance();

    try {
      // Show variant picker
      const variant = await devUtils.showVariantPicker();
      if (!variant) {
        return; // User cancelled
      }

      setIsDeveloperGenerating(true);

      // Generate summary and send notification
      const generatedSummary = await devUtils.generateSummaryAndNotify(variant);

      if (generatedSummary) {
        // Update the displayed summary
        setSummary(generatedSummary);

        // Show success
        devUtils.showSuccessAlert(variant, generatedSummary.headline);
      }
    } catch (error) {
      console.error('[useHomeScreenHandlers] Failed to generate summary:', error);
      devUtils.showErrorAlert(error as Error);
    } finally {
      setIsDeveloperGenerating(false);
    }
  }, [session?.access_token, preferences?.timezone, setSummary, setIsDeveloperGenerating]);

  return {
    handleSummaryAction,
    handleReportSummary,
    handleDeveloperTrigger,
  };
}
